import React, { useEffect, useState } from 'react';
import { Typography, Box, CircularProgress } from "@mui/material";
import ErrorDialog from './ErrorDialog';
import SuccessDialog from './SuccessDialog';
import { notifyAuthExpired, isAuthExpiredError, SESSION_REFRESH_MESSAGE, SESSION_REFRESH_TITLE } from '../../../utils/auth-error-utils';

const AnimatedEllipsis = () => {
  const [dots, setDots] = useState('');

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(dots => (dots.length < 3 ? dots + '.' : ''));
    }, 500); // Adjust the speed as needed

    return () => clearInterval(interval);
  }, []);

  return <span>{dots}</span>;
};

const ApiLinkText = ({
  apiCall,
  onSuccess,
  onError,
  primaryText,
  linkText,
  progressText,
  interimText,
  errorTitle,
  errorMessage,
  successTitle,
  successMessage,
  disabled = false,
  onErrorConfirm = () => { },
  onSuccessConfirm = () => { },
  sx = {},
}) => {
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);
  const [errorTitleOverride, setErrorTitleOverride] = useState(null);
  const [errorMessageOverride, setErrorMessageOverride] = useState(null);
  const [showReloadAction, setShowReloadAction] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const defaultErrorHandler = (error) => {
    const isAuthExpired = isAuthExpiredError(error);
    if (isAuthExpired) {
      notifyAuthExpired({ source: 'api-link-text' });
      return;
    }
    setErrorDetails(isAuthExpired ? null : error.toString());
    setErrorTitleOverride(isAuthExpired ? SESSION_REFRESH_TITLE : null);
    setErrorMessageOverride(isAuthExpired ? SESSION_REFRESH_MESSAGE : null);
    setShowReloadAction(isAuthExpired);
    setOpenErrorDialog(true);
  };

  const defaultSuccessHandler = () => {
    setOpenSuccessDialog(true);
  };

  const handleClick = async () => {
    setIsLoading(true);
    try {
      const result = await apiCall();
      if (onSuccess) {
        onSuccess(result);
      } else {
        defaultSuccessHandler();
      }
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        defaultErrorHandler(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', ...sx }}>
        <Typography component="span">{primaryText}</Typography>
        <Typography
          component="span"
          sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1, fontWeight: 700 }}
          onClick={!isLoading && interimText === '' && !disabled ? handleClick : null}
        >
          {isLoading ? (
            <>
              {progressText}<AnimatedEllipsis />
            </>
          ) : interimText !== '' ? interimText : linkText}
        </Typography>
      </Box>

      <ErrorDialog
        open={openErrorDialog}
        handleClose={() => {
          setErrorDetails(null);
          setErrorTitleOverride(null);
          setErrorMessageOverride(null);
          setShowReloadAction(false);
          setOpenErrorDialog(false);
          onErrorConfirm();
        }}
        title={errorTitleOverride || errorTitle || "Error"}
        message={errorMessageOverride || errorMessage || "An error occurred."}
        details={errorDetails}
        primaryActionText={showReloadAction ? 'Reload Page' : null}
        onPrimaryAction={showReloadAction ? () => window.location.reload() : null}
      />

      <SuccessDialog
        open={openSuccessDialog}
        handleClose={() => {
          setOpenSuccessDialog(false);
          onSuccessConfirm();
        }}
        title={successTitle || "Success"}
        message={successMessage || "Your action was successful."}
      />
    </>
  );
};

export default ApiLinkText;
